import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  profileFormSchema,
  type ProfileFormData,
  type OriginalProfileData,
} from "../../schemas/profile.schema";
import { ProfileChangeDetector } from "../../utils/profile-changes.util";
import { extractErrorMessage } from "@/lib/errors";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";

interface ProfileFormProps {
  originalData: OriginalProfileData;
  onSave: (
    data: ProfileFormData,
    changes: ReturnType<ProfileChangeDetector["detectChanges"]>,
  ) => Promise<void>;
  onCancel: () => void;
  isEditing: boolean;
  isLoading: boolean;
  onEditToggle: () => void;
  isHashKeyUser: boolean;
}

/**
 * Reusable Profile Form Component
 * Follows single responsibility - handles profile data editing with proper validation
 * Implements change detection to optimize API calls
 */
export function ProfileForm({
  originalData,
  onSave,
  onCancel,
  isEditing,
  isLoading,
  onEditToggle,
  isHashKeyUser,
}: ProfileFormProps) {
  const changeDetector = useMemo(
    () => new ProfileChangeDetector(originalData),
    [originalData],
  );

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: originalData.firstName,
      lastName: originalData.lastName,
      email: originalData.email,
    },
    mode: "onChange", // Real-time validation for better UX
  });

  // Update form when original data changes (e.g., after successful save)
  useEffect(() => {
    form.reset({
      firstName: originalData.firstName,
      lastName: originalData.lastName,
      email: originalData.email,
    });
  }, [originalData, form]);

  const currentFormData = form.watch();
  const changes = useMemo(() => {
    return changeDetector.detectChanges(currentFormData);
  }, [currentFormData, changeDetector]);

  const handleSave = async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error("Please fix the validation errors before saving");
      return;
    }

    if (!changes.hasAnyChanges) {
      toast.info("No changes to save");
      return;
    }

    try {
      await onSave(currentFormData, changes);
    } catch (error) {
      const message = extractErrorMessage(error);
      toast.error("Failed to save profile", {
        description: message,
      });
    }
  };

  const handleCancel = () => {
    form.reset({
      firstName: originalData.firstName,
      lastName: originalData.lastName,
      email: originalData.email,
    });
    onCancel();
  };

  return (
    <Form {...form}>
      <div className="w-full space-y-6">
        <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="flex w-full flex-col">
                <FormLabel className="text-sm font-bold text-neutral-800">
                  First Name <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    variant="filled"
                    placeholder="Enter your first name"
                    disabled={!isEditing}
                    className={`${!isEditing ? "bg-gray-50" : ""}`}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="h-5 text-xs leading-tight text-red-600" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="flex w-full flex-col">
                <FormLabel className="text-sm font-bold text-neutral-800">
                  Last Name <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    variant="filled"
                    placeholder="Enter your last name"
                    disabled={!isEditing}
                    className={`${!isEditing ? "bg-gray-50" : ""}`}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="h-5 text-xs leading-tight text-red-600" />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel className="text-sm font-bold text-neutral-800">
                Email Address <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="email"
                  variant="filled"
                  placeholder="Enter your email address"
                  disabled={!isEditing}
                  className={`${!isEditing ? "bg-gray-50" : ""}`}
                  {...field}
                />
              </FormControl>
              <FormMessage className="min-h-[16px] text-xs text-red-600" />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-3 sm:flex-row">
          {!isEditing ? (
            <Button
              type="button"
              variant="outline"
              onClick={onEditToggle}
              className="flex h-[48px] w-full items-center justify-center gap-2 py-3 hover:scale-none sm:w-[200px]"
              disabled={isHashKeyUser}
            >
              <i className="ri-edit-line text-muted text-[16px] font-normal"></i>
              <span className="text-muted text-[16px] leading-normal font-bold">
                Edit Profile
              </span>
            </Button>
          ) : (
            <>
              <Button
                type="button"
                className="flex h-[48px] w-auto items-center justify-center gap-1 py-3 hover:scale-none sm:w-[100px]"
                onClick={handleSave}
                disabled={
                  !changes.hasAnyChanges || isLoading || !form.formState.isValid
                }
              >
                {isLoading ? (
                  <div className="flex h-full items-center justify-center text-white">
                    <Spinner size="sm" className="stroke-white!" />
                  </div>
                ) : (
                  "Save"
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex h-[48px] w-full items-center justify-center gap-1 py-3 hover:scale-none sm:w-[100px]"
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </Form>
  );
}
